import { CommonModule } from '@angular/common';
import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	Injectable,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
	CreatedShare,
	GameData,
	PluginType,
	RendererGameDefinitionService,
	PublicDataServiceProvider,
} from '@meta-press/shared-plugins';
import {
	NgbModal,
	NgbActiveModal,
	NgbDatepickerModule,
	NgbDateParserFormatter,
	NgbDateStruct,
} from '@ng-bootstrap/ng-bootstrap';
import { Export } from '@meta-press/shared-plugins';
import { NotificationService } from '../notification-service/notification.service';
import { ExportService } from '../export-service/export.service';
import { SourceMaterialService } from '../source-material-service/source-material.service';
import { BackendService } from '../backend-service/backend.service';
import { environment } from '../../../environments/environment';

/**
 * Class to hold all data and state for a sharing operation
 */
export class ShareProcess {
	public id = -1;
	public uuid = '';
	public name = '';
	public permissions = 'public'; // 'public' or 'restricted'
	public expirationDate = -1; // -1 means no expiration
	public isSharing = false;
	public isShared = false;
	public isError = false;
	public error: string | null = null; // This will hold any error that occurs during the sharing process
	public sharedUuid = '';
	public isDeleted = false;

	constructor(public exportData: Export) {
		this.name = exportData.name;
	}
}

/**
 * Finnish date formatter for NgbDatepicker
 */
@Injectable()
export class FinnishDateParserFormatter extends NgbDateParserFormatter {
	parse(value: string): NgbDateStruct | null {
		if (value) {
			const dateParts = value.trim().split('.');
			if (dateParts.length === 3) {
				const day = parseInt(dateParts[0], 10);
				const month = parseInt(dateParts[1], 10);
				const year = parseInt(dateParts[2], 10);
				if (day && month && year) {
					return { day, month, year };
				}
			}
		}
		return null;
	}

	format(date: NgbDateStruct | null): string {
		if (date) {
			return `${date.day}.${date.month}.${date.year}`;
		}
		return '';
	}
}

/**
 * ShareModalComponent - Reusable modal for managing game shares
 *
 * This component handles creating, viewing, editing, and deleting shares for an export.
 * It can be opened programmatically from any component.
 */
@Component({
	selector: 'app-share-modal',
	standalone: true,
	imports: [CommonModule, FormsModule, NgbDatepickerModule],
	providers: [
		{ provide: NgbDateParserFormatter, useClass: FinnishDateParserFormatter },
	],
	templateUrl: './share-modal.component.html',
	styleUrls: ['./share-modal.component.scss'],
})
export class ShareModalComponent implements OnInit {
	// Input: The export data to share
	@Input() exportData!: Export;

	// Output: Emits when a share is successfully created
	@Output() shareCreated = new EventEmitter<CreatedShare>();

	// Share process state
	activeShareProcess: ShareProcess | null = null;

	// Date/time fields for new share creation
	expirationDatePicker: NgbDateStruct | null = null;
	expirationHour = 23;
	expirationMinute = 59;
	hasExpiryDate = false;

	// Shares for current export
	currentExportShares: CreatedShare[] = [];
	primaryShare: CreatedShare | null = null;
	legacyShares: CreatedShare[] = [];

	// Edit mode for primary share expiration
	isEditingExpirationDate = false;
	editExpirationDatePicker: NgbDateStruct | null = null;
	editExpirationHour = 23;
	editExpirationMinute = 59;
	editHasExpiryDate = false;

	// Loading/deleting states
	shareBeingUpdated: number | null = null;
	sharesBeingDeletedInModal: Set<number> = new Set<number>();
	sharesBeingLoadedInModal: Set<number> = new Set<number>();

	// Sorting for shares table
	sharesSortColumn: 'name' | 'expirationDate' | 'creationDate' | null = null;
	sharesSortDirection: 'asc' | 'desc' = 'desc';

	constructor(
		public activeModal: NgbActiveModal,
		private modalService: NgbModal,
		private exportService: ExportService,
		private backendService: BackendService,
		private notificationService: NotificationService,
		private sourceMaterialService: SourceMaterialService,
		private publicDataService: PublicDataServiceProvider,
	) {}

	ngOnInit(): void {
		// Initialize the share process
		this.activeShareProcess = new ShareProcess(this.exportData);

		// Initialize date and time fields with default values
		const tomorrow = new Date();
		tomorrow.setDate(tomorrow.getDate() + 1);

		this.expirationDatePicker = {
			year: tomorrow.getFullYear(),
			month: tomorrow.getMonth() + 1,
			day: tomorrow.getDate(),
		};
		this.expirationHour = 23;
		this.expirationMinute = 59;
		this.hasExpiryDate = false;

		// Load all shares for this export
		this.loadSharesForExport(this.exportData.id);

		// Load primary and legacy shares
		this.primaryShare = this.getPrimaryShare(this.exportData.id);
		this.legacyShares = this.getLegacyShares(this.exportData.id);

		// Always start with a new share (never pre-populate from existing)
		this.activeShareProcess.expirationDate = -1;
	}

	/**
	 * Get the primary (most recent) share for an export
	 */
	getPrimaryShare(exportId: number): CreatedShare | null {
		const shares = this.exportService.GetSharedExperiencesForExport(exportId);
		if (shares.length === 0) return null;

		// Return most recent share (newest creationDate)
		return shares.sort((a, b) => b.creationDate - a.creationDate)[0];
	}

	/**
	 * Get legacy shares for an export (all shares except the primary/most recent)
	 * Returns empty array if there are 0 or 1 shares
	 */
	getLegacyShares(exportId: number): CreatedShare[] {
		const shares = this.exportService.GetSharedExperiencesForExport(exportId);
		if (shares.length <= 1) return [];

		const sorted = shares.sort((a, b) => b.creationDate - a.creationDate);
		return sorted.slice(1); // All except the most recent
	}

	/**
	 * Load all shares for the specified export
	 */
	private loadSharesForExport(exportId: number): void {
		// Allow 0 as a valid exportId (for pseudo exports with experienceId: 0)
		if (exportId === undefined || exportId === null || exportId === -1) {
			this.currentExportShares = [];
			return;
		}

		// Get shares from export service
		this.currentExportShares =
			this.exportService.GetSharedExperiencesForExport(exportId);
	}

	/**
	 * Get filtered and sorted shares for the current export
	 */
	get filteredCurrentExportShares(): CreatedShare[] {
		const shares = [...this.currentExportShares];

		// Apply sorting
		return this.applySharesSorting(shares);
	}

	/**
	 * Update the expirationDate field with combined date and time as UNIX timestamp
	 */
	updateExpirationDateTime(): void {
		if (!this.activeShareProcess) return;

		if (this.hasExpiryDate && this.expirationDatePicker) {
			// Create a date from the NgbDateStruct
			const date = new Date(
				this.expirationDatePicker.year,
				this.expirationDatePicker.month - 1, // JavaScript months are 0-indexed
				this.expirationDatePicker.day,
				this.expirationHour || 0,
				this.expirationMinute || 0,
				0, // seconds
			);

			// Convert to UNIX timestamp (seconds, not milliseconds)
			this.activeShareProcess.expirationDate = Math.floor(
				date.getTime() / 1000,
			);
		} else {
			// No expiry date, set to -1
			this.activeShareProcess.expirationDate = -1;
		}
	}

	/**
	 * Handle checkbox change for expiry date
	 */
	onExpiryDateCheckboxChange(): void {
		if (!this.activeShareProcess) return;

		if (this.hasExpiryDate) {
			// Checkbox was just checked, update the expiration date
			this.updateExpirationDateTime();
		} else {
			// Checkbox was unchecked, set to -1
			this.activeShareProcess.expirationDate = -1;
		}
	}

	/**
	 * Start sharing to server (create new share or update existing)
	 */
	async shareExperience(): Promise<void> {
		if (!this.activeShareProcess) return;

		// Check if we're updating an existing share or creating a new one
		const isUpdatingExistingShare = this.primaryShare !== null;

		// Update expiration date based on checkbox state before sharing
		if (this.hasExpiryDate) {
			this.updateExpirationDateTime();
		} else {
			this.activeShareProcess.expirationDate = -1;
		}

		if (
			!this.activeShareProcess.exportData ||
			!this.activeShareProcess.exportData.gameData
		) {
			this.notificationService.ShowToast(
				'Virhe: Julkaisusta puuttuu pelidata.',
				'danger',
			);
			console.error(
				'Cannot share experience without exportData or gameData:',
				this.activeShareProcess.exportData,
			);
			return;
		}

		this.activeShareProcess.isSharing = true;

		// Ensure GameData has methods using RendererGameDefinitionService
		const gameDefinitionService = new RendererGameDefinitionService();
		const gameType = this.activeShareProcess?.exportData.gameData?.type;
		const rendererGameDefinition = gameType
			? await gameDefinitionService.getGameDefinition(gameType)
			: undefined;

		if (!rendererGameDefinition) {
			this.notificationService.ShowToast(
				'Virhe: Julkaisun pelityyppiä ei löytynyt',
				'danger',
			);
			console.error(
				'Editor game definition not found for type:',
				this.activeShareProcess.exportData.gameData?.type,
			);
			this.activeShareProcess.isSharing = false;
			return;
		}

		// We need to ensure that the gameData has methods added to it
		GameData.addMethods(
			this.activeShareProcess.exportData.gameData,
			rendererGameDefinition.gameDataType,
		);

		// Build the GameData types map by loading constructors for all available types
		const availableTypes = gameDefinitionService.AvailablePluginTypes;
		const gameDataTypesMap = new Map<PluginType, new () => GameData>();

		for (const pluginType of availableTypes) {
			try {
				const constructor =
					await gameDefinitionService.getGameDataConstructor(
						pluginType,
					);
				if (constructor) {
					gameDataTypesMap.set(pluginType, constructor);
				}
			} catch (error) {
				console.error(
					`Failed to load constructor for type ${pluginType}:`,
					error,
				);
			}
		}

		this.activeShareProcess.exportData.gameData.addPrototypesToAllGameData(
			gameDataTypesMap,
		);

		const usedArticleIds = Array.from(
			new Set(
				this.activeShareProcess.exportData.gameData?.GetArticlesContainedInGameDataObjectsRecursively(
					true,
				) || [],
			),
		).filter((id) => {
			const article = this.sourceMaterialService.GetArticleById(id);

			return (
				article &&
				(article.type == 'image' ||
					article.type == 'audio' ||
					article.type == 'pdf')
			);
		});

		// Use user-given name
		this.activeShareProcess.exportData.name = this.activeShareProcess.name;

		// Check if we're updating an existing share or creating a new one
		if (isUpdatingExistingShare && this.primaryShare) {
			// Update existing share
			this.exportService
				.updateSharedExperience(
					this.primaryShare,
					this.activeShareProcess.exportData,
					usedArticleIds,
				)
				.subscribe({
					next: (response: CreatedShare) => {
						if (this.activeShareProcess) {
							this.activeShareProcess.isSharing = false;
							this.activeShareProcess.isShared = true;
							this.activeShareProcess.sharedUuid = response.uuid;
							this.activeShareProcess.id = response.id;
							this.activeShareProcess.expirationDate =
								response.expirationDate;

							// Refresh the shares list to show the updated share
							this.refreshSharesAfterCreation();

							this.notificationService.ShowToast(
								'Julkaisu päivitetty onnistuneesti',
								'success',
							);

							// Emit the share created/updated event
							this.shareCreated.emit(response);
						} else {
							this.notificationService.ShowToast(
								'Virhe julkaisun päivityksessä',
								'danger',
							);
							console.error('Error: activeShareProcess is null');
						}
					},
					error: (error: unknown) => {
						if (this.activeShareProcess) {
							this.activeShareProcess.isSharing = false;
							this.activeShareProcess.isError = true;
							this.activeShareProcess.error =
								error instanceof Error
									? error.message
									: 'Tuntematon virhe';
						}

						this.notificationService.ShowToast(
							'Virhe julkaisun päivityksessä',
							'danger',
						);
						console.error('Error updating share:', error);
					},
				});
		} else {
			// Create new share
			this.exportService
				.ShareExport(this.activeShareProcess, usedArticleIds)
				.subscribe({
					next: (response: CreatedShare) => {
						if (this.activeShareProcess) {
							this.activeShareProcess.isSharing = false;
							this.activeShareProcess.isShared = true;
							this.activeShareProcess.sharedUuid = response.uuid;
							this.activeShareProcess.id = response.id;
							this.activeShareProcess.expirationDate =
								response.expirationDate;

							// Refresh the shares list to show the new share
							this.refreshSharesAfterCreation();

							// Reset the form for next share creation
							this.resetShareFormAfterCreation();

							this.notificationService.ShowToast(
								'Peli julkaistu!',
								'success',
							);

							// Emit the share created event
							this.shareCreated.emit(response);
						} else {
							this.notificationService.ShowToast(
								'Virhe pelin julkaisussa',
								'danger',
							);
							console.error('Error: activeShareProcess is null');
						}
					},
					error: (error: unknown) => {
						if (this.activeShareProcess) {
							this.activeShareProcess.isSharing = false;
							this.activeShareProcess.isError = true;
							this.activeShareProcess.error =
								error instanceof Error
									? error.message
									: 'Tuntematon virhe';
						}

						this.notificationService.ShowToast(
							'Virhe pelin julkaisussa',
							'danger',
						);
						console.error('Error sharing experience:', error);
					},
				});
		}
	}

	/**
	 * Start editing the expiration date of the primary share
	 */
	startEditingExpirationDate(): void {
		if (!this.primaryShare) return;

		this.isEditingExpirationDate = true;

		// Populate edit fields with current expiration date
		if (
			this.primaryShare.expirationDate &&
			this.primaryShare.expirationDate !== -1
		) {
			this.editHasExpiryDate = true;
			const expirationDate = new Date(
				this.primaryShare.expirationDate * 1000,
			);

			this.editExpirationDatePicker = {
				year: expirationDate.getFullYear(),
				month: expirationDate.getMonth() + 1,
				day: expirationDate.getDate(),
			};
			this.editExpirationHour = expirationDate.getHours();
			this.editExpirationMinute = expirationDate.getMinutes();
		} else {
			// No expiration date set
			this.editHasExpiryDate = false;
			const tomorrow = new Date();
			tomorrow.setDate(tomorrow.getDate() + 1);

			this.editExpirationDatePicker = {
				year: tomorrow.getFullYear(),
				month: tomorrow.getMonth() + 1,
				day: tomorrow.getDate(),
			};
			this.editExpirationHour = 23;
			this.editExpirationMinute = 59;
		}
	}

	/**
	 * Cancel editing the expiration date
	 */
	cancelEditingExpirationDate(): void {
		this.isEditingExpirationDate = false;
		this.shareBeingUpdated = null;
		this.editExpirationDatePicker = null;
		this.editExpirationHour = 23;
		this.editExpirationMinute = 59;
		this.editHasExpiryDate = false;
	}

	/**
	 * Update the edit expiration date/time based on edit fields
	 */
	updateEditExpirationDateTime(): number {
		if (this.editHasExpiryDate && this.editExpirationDatePicker) {
			const date = new Date(
				this.editExpirationDatePicker.year,
				this.editExpirationDatePicker.month - 1,
				this.editExpirationDatePicker.day,
				this.editExpirationHour || 0,
				this.editExpirationMinute || 0,
				0,
			);
			return Math.floor(date.getTime() / 1000);
		}
		return -1; // No expiration
	}

	/**
	 * Save the edited expiration date to the server
	 */
	saveExpirationDate(): void {
		if (!this.primaryShare || this.shareBeingUpdated !== null) return;

		const newExpirationDate = this.updateEditExpirationDateTime();

		// Set loading state
		this.shareBeingUpdated = this.primaryShare.id;

		// Call backend to update the share
		this.backendService
			.UpdateShare(this.primaryShare.id, true, newExpirationDate)
			.subscribe({
				next: () => {
					this.notificationService.ShowToast(
						'Vanhenemispäivä päivitetty',
						'success',
					);

					// Update the local share object
					if (this.primaryShare) {
						this.primaryShare.expirationDate = newExpirationDate;
					}

					// Refresh shares from service
					if (this.activeShareProcess?.exportData.id !== undefined) {
						this.primaryShare = this.getPrimaryShare(
							this.activeShareProcess.exportData.id,
						);
						this.legacyShares = this.getLegacyShares(
							this.activeShareProcess.exportData.id,
						);
					}

					// Clear loading state and exit edit mode
					this.shareBeingUpdated = null;
					this.cancelEditingExpirationDate();
				},
				error: (error) => {
					console.error('Error updating expiration date:', error);
					this.notificationService.ShowToast(
						'Virhe vanhenemispäivän päivityksessä',
						'danger',
					);
					// Clear loading state on error
					this.shareBeingUpdated = null;
				},
			});
	}

	/**
	 * Delete a share from the modal
	 */
	deleteShareFromModal(share: CreatedShare): void {
		if (!share.id) return;

		// Add to being deleted set
		this.sharesBeingDeletedInModal.add(share.id);

		this.exportService.DeleteShare(share.id).subscribe({
			next: () => {
				// Remove from being deleted set
				this.sharesBeingDeletedInModal.delete(share.id);

				// Remove from current shares array
				this.currentExportShares = this.currentExportShares.filter(
					(s) => s.id !== share.id,
				);

				// Refresh primary and legacy shares after deletion
				if (this.activeShareProcess?.exportData.id) {
					this.primaryShare = this.getPrimaryShare(
						this.activeShareProcess.exportData.id,
					);
					this.legacyShares = this.getLegacyShares(
						this.activeShareProcess.exportData.id,
					);
				}

				this.notificationService.ShowToast('Julkaisu päätetty', 'success');

				// Close the modal after successful deletion
				this.activeModal.close();
			},
			error: (error: unknown) => {
				// Remove from being deleted set even on error
				this.sharesBeingDeletedInModal.delete(share.id);

				console.error('Error deleting share:', error);
				this.notificationService.ShowToast(
					'Virhe julkaisun poistossa',
					'danger',
				);
			},
		});
	}

	/**
	 * Copy shared link to clipboard
	 */
	copySharedLinkToClipboardFromModal(share: CreatedShare): void {
		const link = this.GetShareURL(share.uuid);
		this.copySharedLinkToClipboard(link);
	}

	/**
	 * Copy iframe embed code to clipboard
	 */
	copyEmbedCodeToClipboardFromModal(share: CreatedShare): void {
		const embedCode = this.GetShareIFrameCode(share.uuid);
		this.copySharedLinkToClipboard(embedCode);
	}

	/**
	 * Preview/play a shared export from modal
	 */
	previewShareFromModal(share: CreatedShare): void {
		const shareUrl = this.GetShareURL(share.uuid);
		this.openSharedLinkInNewWindow(shareUrl);
	}

	/**
	 * Play a shared export from modal using download-first pattern
	 */
	playShareFromModal(share: CreatedShare): void {
		// Start loading
		this.sharesBeingLoadedInModal.add(share.id);

		// Get the shared experience data using public backend service
		this.publicDataService.GetExperienceByUuid(share.uuid).subscribe({
			next: async (experience) => {
				try {
					if (!experience) {
						this.notificationService.ShowToast(
							'Kokemusta ei löytynyt',
							'danger',
						);
						this.sharesBeingLoadedInModal.delete(share.id);
						return;
					}

					const gameData = JSON.parse(experience.jsonBlob);
					GameData.restoreMaps(null, gameData);

					if (!gameData || !gameData.type) {
						this.notificationService.ShowToast(
							'Pelidataa ei löydy',
							'danger',
						);
						this.sharesBeingLoadedInModal.delete(share.id);
						return;
					}

					// Find the renderer component for this game type using RendererGameDefinitionService
					const gameDefinitionService =
						new RendererGameDefinitionService();
					const gameDefinition =
						await gameDefinitionService.getGameDefinition(
							gameData.type,
						);

					if (!gameDefinition || !gameDefinition.rendererComponent) {
						this.notificationService.ShowToast(
							'Renderöijää ei löydy tälle pelityypille',
							'danger',
						);
						this.sharesBeingLoadedInModal.delete(share.id);
						return;
					}

					// Validate game data before opening preview
					const validationResult =
						await gameDefinitionService.validateGameDataForImport(
							gameData.type,
							gameData,
						);

					if (!validationResult.isValid) {
						// Show the specific validation error(s)
						const errorMessage =
							validationResult.errors.length > 0
								? `Validointivirhe: ${validationResult.errors.join(', ')}`
								: 'Sisällössä on validointivirheitä. Esikatselu ei ole mahdollinen.';

						this.notificationService.ShowToast(errorMessage, 'danger');
						this.sharesBeingLoadedInModal.delete(share.id);
						return;
					}

					// Create export-like object for the renderer
					const exportData: Export = {
						id: experience.id,
						uuid: experience.uuid,
						name: experience.name,
						date: experience.createdUnixTime,
						creator: experience.userId,
						version: experience.version,
						gameData: gameData,
					};

					// Set the default shareUuid in PublicDataServiceProvider for fallback scenarios
					this.publicDataService.SetDefaultShareUuid(share.uuid);

					// Load and open the renderer component
					const componentType =
						await gameDefinition.rendererComponent();

					const modalRef = this.modalService.open(componentType, {
						size: 'xl',
						windowClass: `dark-modal ${gameDefinition.displayMode == 'fullscreen' || (gameDefinition.displayMode == 'both' && gameDefinition.defaultDisplayMode == 'fullscreen') ? 'modal-xxl' : ''}`,
					});

					if (modalRef && modalRef.componentInstance) {
						// Set the export data for the renderer
						modalRef.componentInstance.exportData = exportData;

						// Pass shareId directly to renderer component for direct access
						modalRef.componentInstance.shareId = share.uuid;
					}

					// Loading completed successfully - modal is now open
					this.sharesBeingLoadedInModal.delete(share.id);
				} catch (error) {
					console.error('Error playing shared export:', error);
					this.notificationService.ShowToast(
						'Virhe toistaessa jaettua kokemusta',
						'danger',
					);
					this.sharesBeingLoadedInModal.delete(share.id);
				}
			},
			error: (error: unknown) => {
				console.error('Error loading shared experience:', error);
				this.notificationService.ShowToast(
					'Virhe ladattaessa jaettua kokemusta',
					'danger',
				);
				this.sharesBeingLoadedInModal.delete(share.id);
			},
		});
	}

	/**
	 * Check if a share is expired
	 */
	isShareExpired(share: CreatedShare): boolean {
		if (!share.expirationDate || share.expirationDate === -1) {
			return false; // No expiration date set
		}

		const now = Math.floor(Date.now() / 1000);
		return share.expirationDate <= now;
	}

	/**
	 * Refresh shares list after creating a new share
	 */
	private refreshSharesAfterCreation(): void {
		if (this.activeShareProcess?.exportData.id) {
			this.loadSharesForExport(this.activeShareProcess.exportData.id);
			// Also refresh primary and legacy shares
			this.primaryShare = this.getPrimaryShare(
				this.activeShareProcess.exportData.id,
			);
			this.legacyShares = this.getLegacyShares(
				this.activeShareProcess.exportData.id,
			);
		}
	}

	/**
	 * Reset share form after successful creation for next share
	 */
	private resetShareFormAfterCreation(): void {
		// Reset form fields for creating another share
		const tomorrow = new Date();
		tomorrow.setDate(tomorrow.getDate() + 1);

		this.expirationDatePicker = {
			year: tomorrow.getFullYear(),
			month: tomorrow.getMonth() + 1,
			day: tomorrow.getDate(),
		};
		this.expirationHour = 23;
		this.expirationMinute = 59;
		this.hasExpiryDate = false;

		// Reset the share process state but keep the export data
		if (this.activeShareProcess) {
			const exportData = this.activeShareProcess.exportData;
			this.activeShareProcess = new ShareProcess(exportData);
			this.activeShareProcess.expirationDate = -1;
		}
	}

	/**
	 * Get share URL
	 */
	GetShareURL(uuid: string): string {
		const currentUrl = window.location.href;
		const urlBase = new URL('..', currentUrl).href;
		// Remove trailing slash if present
		const baseUrlWithoutTrailingSlash = urlBase.endsWith('/')
			? urlBase.slice(0, -1)
			: urlBase;

		const dataVersion = environment.appDataVersion;
		return `${baseUrlWithoutTrailingSlash}/viewers/${dataVersion}/#/?uuid=${uuid}`;
	}

	/**
	 * Get share iframe embed code
	 */
	GetShareIFrameCode(uuid: string): string {
		const link = this.GetShareURL(uuid);
		const iframeCode = `<iframe src="${link}" width="100%" height="100%" frameborder="0" allowfullscreen></iframe>`;
		return iframeCode;
	}

	/**
	 * Copy text to clipboard
	 */
	copySharedLinkToClipboard(link: string): void {
		const linkToCopy = link;
		if (navigator.clipboard) {
			navigator.clipboard
				.writeText(linkToCopy)
				.then(() => {
					this.notificationService.ShowToast(
						'Kopioitu leikepöydälle',
						'success',
					);
				})
				.catch((err: unknown) => {
					console.error('Failed to copy:', err);
					this.notificationService.ShowToast(
						'Kopiointi epäonnistui',
						'danger',
					);
				});
		} else {
			// Fallback for older browsers
			const textArea = document.createElement('textarea');
			textArea.value = linkToCopy;
			textArea.style.position = 'fixed';
			textArea.style.left = '0';
			textArea.style.top = '0';
			textArea.style.opacity = '0';
			document.body.appendChild(textArea);
			textArea.focus();
			textArea.select();
			try {
				document.execCommand('copy');
				this.notificationService.ShowToast(
					'Kopioitu leikepöydälle',
					'success',
				);
			} catch (err) {
				console.error('Fallback: Oops, unable to copy', err);
				this.notificationService.ShowToast(
					'Kopiointi epäonnistui',
					'danger',
				);
			}
			document.body.removeChild(textArea);
		}
	}

	/**
	 * Open link in new window
	 */
	openSharedLinkInNewWindow(link: string): void {
		window.open(link, '_blank');
	}

	/**
	 * Sort shares by the specified column
	 */
	sortModalShares(column: 'name' | 'expirationDate' | 'creationDate'): void {
		if (this.sharesSortColumn === column) {
			// Toggle direction if clicking same column
			this.sharesSortDirection =
				this.sharesSortDirection === 'asc' ? 'desc' : 'asc';
		} else {
			// Set new column and default to descending (newest first)
			this.sharesSortColumn = column;
			this.sharesSortDirection = 'desc';
		}
	}

	/**
	 * Apply sorting to shares array
	 */
	private applySharesSorting(shares: CreatedShare[]): CreatedShare[] {
		if (!this.sharesSortColumn) {
			// Default sort by expiration date descending (newest first)
			return shares.sort((a, b) => {
				const aDate = a.expirationDate || 0;
				const bDate = b.expirationDate || 0;
				return bDate - aDate;
			});
		}

		return shares.sort((a, b) => {
			let aValue: string | number;
			let bValue: string | number;

			switch (this.sharesSortColumn) {
				case 'name':
					// Use share name if available, otherwise use export name
					aValue = (
						a.name ||
						this.activeShareProcess?.exportData.name ||
						''
					).toLowerCase();
					bValue = (
						b.name ||
						this.activeShareProcess?.exportData.name ||
						''
					).toLowerCase();
					break;
				case 'expirationDate':
					// Sort by expiration date (Unix timestamp in seconds)
					aValue = Number(a.expirationDate || 0);
					bValue = Number(b.expirationDate || 0);
					break;
				case 'creationDate':
					// Sort by creation date (Unix timestamp in seconds)
					aValue = Number(a.creationDate || 0);
					bValue = Number(b.creationDate || 0);
					break;
				default:
					return 0;
			}

			let comparison = 0;
			if (aValue < bValue) {
				comparison = -1;
			} else if (aValue > bValue) {
				comparison = 1;
			}

			return this.sharesSortDirection === 'asc'
				? comparison
				: -comparison;
		});
	}

	/**
	 * Get sort icon for shares table
	 */
	getSharesSortIcon(
		column: 'name' | 'expirationDate' | 'creationDate',
	): string {
		if (this.sharesSortColumn !== column) {
			return 'bi-arrow-down-up'; // Default sort icon
		}
		if (column === 'expirationDate' || column === 'creationDate') {
			return this.sharesSortDirection === 'asc'
				? 'bi-sort-numeric-down'
				: 'bi-sort-numeric-up';
		}
		return this.sharesSortDirection === 'asc'
			? 'bi-sort-alpha-down'
			: 'bi-sort-alpha-up';
	}
}
